import React, { createRef, useContext, useState } from 'react';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { callFunction } from '../../lib/Helpers';
import { HelpContainer, HelpContent, HelpHeader } from './styles';
import { CheckCircle, PaperPlaneTilt, XCircle } from '@phosphor-icons/react';
import { Spinner } from '@fluentui/react';
import { TeamsFxContext } from '../../Context';
import { useTranslation } from 'react-i18next';

export enum SendStatus {
  None,
  Loading,
  Sent,
  Failed,
}

export const Help = () => {
  const appInsights = useAppInsightsContext();
  const teamsUserCredential = useContext(TeamsFxContext).teamsUserCredential;

  const { t } = useTranslation('settings');

  const [sendStatus, setSendStatus] = useState<SendStatus>(SendStatus.None);

  const refSelect = createRef<HTMLSelectElement>();
  const refText = createRef<HTMLTextAreaElement>();

  const sendFeedback = (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    setSendStatus(SendStatus.Loading);
    if (refText.current) {
      if (refText.current.value.length > 0) {
        callFunction(
          true,
          'sendFeedback?{0}&message=' +
            refText.current.value +
            '&type=' +
            refSelect.current?.selectedOptions.item(0)?.value,
          undefined,
          false,
          teamsUserCredential
        )
          .then(() => {
            setSendStatus(SendStatus.Sent);
            if (refSelect.current) refSelect.current.selectedIndex = 0;
          })
          .catch((error) => {
            if (process.env.REACT_APP_APP_INSIGHTS !== '')
              appInsights.trackException({
                exception: error,
                properties: { component: 'Help', location: 'onSendFeedback' },
              });
            setSendStatus(SendStatus.Failed);
          });
        refText.current.value = '';
      }
    }
  };

  const onTextChange = () => {
    setSendStatus(SendStatus.None);
  };

  const renderStatus = () => {
    switch (sendStatus) {
      case SendStatus.None:
        return <></>;
      case SendStatus.Loading:
        return <Spinner size={1} />;
      case SendStatus.Sent:
        return (
          <span id="messagesent" className="statusMessage">
            <CheckCircle weight="duotone" />
            {t('HELP_SEND_SUCCESS')}
          </span>
        );
      case SendStatus.Failed:
        return (
          <span id="messagefailed" className="statusMessage">
            <XCircle weight="duotone" />
            {t('HELP_SEND_FAILED')}
          </span>
        );
      default:
        return <></>;
    }
  };

  return (
    <HelpContainer>
      <HelpHeader>
        <h1>{t('HELP_TITLE')}</h1>
        <h2>{t('HELP_SUBTITLE')}</h2>
      </HelpHeader>
      <HelpContent>
        <section>
          <p>{t('HELP_DESCRIPTION')}</p>
        </section>

        <section>
          <h2>{t('HELP_CONTACT_HEADER')}</h2>
          <p>
            {t('HELP_CONTACT_DESCRIPTION')}
            <a href="mailto:support@bitfractal.com">support@bitfractal.com</a>
          </p>
        </section>

        <section>
          <h2>{t('HELP_SHARE_HEADER')}</h2>
          <p>{t('HELP_SHARE_DESCRIPTION')}</p>
          <form onSubmit={(ev) => sendFeedback(ev)}>
            <label htmlFor="feedbacktype">
              {t('HELP_FEEDBACK_TYPE_LABEL')}
            </label>
            <select
              ref={refSelect}
              id="feedbacktype"
              className="ideaoptions"
              name="list"
            >
              <option className="ideaoptions">
                {t('HELP_FEEDBACK_OPTION')}
              </option>
              <option className="ideaoptions">{t('HELP_IDEA_OPTION')}</option>
            </select>

            <label htmlFor="feedbacktext">{t('HELP_MESSAGE_FORM_LABEL')}</label>
            <textarea
              ref={refText}
              id="feedbacktext"
              name="message"
              onChange={onTextChange}
            />
            <footer>
              <button type="submit">
                {t('HELP_SEND_BUTTON')} <PaperPlaneTilt weight="duotone" />
              </button>
              {renderStatus()}
            </footer>
          </form>
        </section>
      </HelpContent>
    </HelpContainer>
  );
};
